import { AppDataState, AppId, AppThunkAPIType, unknownError } from "../../app/appTypes";
import { createAsyncThunk, createEntityAdapter, createSlice, EntityState } from "@reduxjs/toolkit";
import { deleteWithAuth, getWithAuth, postWithAuth, putWithAuth } from "../../http";
import { RootState } from "../../app/store";
import { QUICK_TAG_LABEL, QuickTagEnum } from "../administration/companyProfile/enums/QuickTagEnum";

export interface Supplier {
    id: AppId,
    companyId: AppId,
    name: string,
    shortName: string,
    address1: string,
    address2: string,
    city: string,
    state: string,
    zipcode: string,
    country: string,
    contactPerson: string,
    email: string,
    phone: string,
    mobile: string, 
    supplierType: SupplierTypeEnum,
    status: "ACTIVE" | "CLOSED",
    quickTags: SupplierQuickTag[],
    rating: SupplierRating[]
}

export interface SupplierQuickTag {
    id: number;
    supplierId: number;
    quickTag: keyof typeof QuickTagEnum;
    quickTagId: QuickTagEnum;
}

export interface SupplierRating {
    id: number,
    supplierId: number,
    rating: number,
    description: string,
    createDate: Date,
    createdBy: string
}

export type DraftSupplier = Omit<Supplier, "id" | "companyId" | "status" | "supplierType" | "rating" | "quickTags"> & {
    supplierType: SupplierTypeEnum | ""
    quickTags: number[]
    structureId?: string
}

export type UpdateSupplierPayload = DraftSupplier & {
    id: AppId
}

export interface SupplierStructure {
    supplierId: AppId
    structureId: AppId

}

export interface SupplierRatingPayload {
    supplierId: number
    rating: number
    description: string
}

type SupplierQuickTagPayload = {
    quickTag: number
}

export enum SupplierTypeEnum {
    Manufacturer = "Manufacturer",
    Integrator = "Integrator",
    Distributor = "Distributor",
    ServiceProvider = "ServiceProvider",
    SoftwareVendor = "SoftwareVendor",
}

export const SUPPLIER_TYPE_OPTIONS = Object.values(SupplierTypeEnum).map((value) => ({
    id: value,
    label: value,
}))

export const getQuickTagShortLabel = (tag: Supplier["quickTags"][number]) => {
    return QUICK_TAG_LABEL[tag.quickTagId] ?? "-"
}

const mapQuickTagsToPayload = (quickTags?: number[]): SupplierQuickTagPayload[] => {
    return Array.from(new Set(quickTags ?? [])).map((id) => ({ quickTag: id }))
}

const adapter = createEntityAdapter<Supplier>({
    selectId: (supplier) => supplier.id,
})

export type SuppliersState = EntityState<Supplier> & { state: AppDataState }

const initialState: SuppliersState = adapter.getInitialState({ state: { type: "empty" } })
export const loadSuppliers = createAsyncThunk<Supplier[], void, AppThunkAPIType>("suppliers/load", async (_, { dispatch, rejectWithValue }) => {
    const result = await dispatch(getWithAuth({ url: "api/suppliers"}))
    const { payload } = result
    if (getWithAuth.fulfilled.match(result)) {
        return payload as Supplier[]
    } else {
        return rejectWithValue(payload ?? { kind: 'unknown' })
    }
})
export const loadSupplier = createAsyncThunk<Supplier, AppId, AppThunkAPIType>("suppliers/load-one", async (supplierId, { dispatch, rejectWithValue }) => {
    const result = await dispatch(getWithAuth({ url: `api/suppliers/${supplierId}` }))
    const { payload } = result
    if (getWithAuth.fulfilled.match(result)) {
        return payload as Supplier
    } else {
        return rejectWithValue(payload ?? { kind: 'unknown' })
    }
})
export const loadSuppliersByStructure = createAsyncThunk<Supplier[], AppId[], AppThunkAPIType>("suppliers/load-by-structure", async (structureIds, { dispatch, rejectWithValue }) => {
    const result = await dispatch(postWithAuth({ url: "api/suppliers/search", payload: { structureIds } }))
    const { payload } = result
    if (postWithAuth.fulfilled.match(result)) {
        return payload as Supplier[]
    } else {
        return rejectWithValue(payload ?? { kind: 'unknown' })
    }
})
export const createSupplier = createAsyncThunk<Supplier, DraftSupplier, AppThunkAPIType>
    ("suppliers/create", async (supplier: DraftSupplier, { dispatch, rejectWithValue }) => {
        const { quickTags, ...supplierData } = supplier
        const result = await dispatch(postWithAuth({ 
            url: "api/suppliers",
            payload: {
                ...supplierData,
                quickTags: mapQuickTagsToPayload(quickTags),
            },
        }))
        const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
            return payload as Supplier
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)
export const updateSupplier = createAsyncThunk<Supplier, UpdateSupplierPayload, AppThunkAPIType>(
    "suppliers/update", 
    async (supplier: UpdateSupplierPayload, { dispatch, rejectWithValue }) => {
        const { quickTags, ...supplierData } = supplier
        const result = await dispatch(putWithAuth({ 
            url: `api/suppliers/${supplier.id}`,
            payload: {
                ...supplierData,
                quickTags: mapQuickTagsToPayload(quickTags),
            },
        }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return payload as Supplier
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
})

export const deleteSupplier = createAsyncThunk<AppId, AppId, AppThunkAPIType>(
    "suppliers/delete",
    async (supplierId, { rejectWithValue, dispatch }) => {
        const result = await dispatch(deleteWithAuth({ url: `api/suppliers/${supplierId}` }))
        const { payload } = result 
        if (deleteWithAuth.fulfilled.match(result)) {
            return supplierId
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const saveSupplierRating = createAsyncThunk<SupplierRating, SupplierRatingPayload, AppThunkAPIType>(
    "suppliers/save-rating",
    async (rating, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({
            url: `api/suppliers/${rating.supplierId}/rating`,
            payload: rating,
        }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return payload as SupplierRating
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const loadSuppliersStructures = createAsyncThunk<SupplierStructure[], void, AppThunkAPIType>(
    "suppliers_structures_access/load_all",
    async (_, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({
            url: `api/supplier-structure`,
        }))

        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as SupplierStructure[]
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const loadStructureSuppliers = createAsyncThunk<SupplierStructure[], AppId, AppThunkAPIType>(
    "supplier_structure_access/load_by_structure",
    async (structureId, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({
            url: `api/supplier-structure/structure/${structureId}`,
        }))

        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as SupplierStructure[]
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const loadSupplierStructure = createAsyncThunk<SupplierStructure[], AppId, AppThunkAPIType>(
    "supplier_structure_access/load",
    async (supplierId, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({
            url: `api/supplier-structure/${supplierId}`,
        }))

        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as SupplierStructure[]
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const saveSupplierStructure = createAsyncThunk<SupplierStructure[], [AppId, SupplierStructure[]], AppThunkAPIType>(
    "supplier_structure_access/save",
    async ([supplierId, items], { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({
            url: `api/supplier-structure/${supplierId}`,
            payload: items,
        }))
        if (putWithAuth.fulfilled.match(result)) {
            return items
        } else {
            const { payload } = result
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const closeSupplierCollaboration = createAsyncThunk<Supplier, AppId, AppThunkAPIType>(
    "suppliers/close-collaboration",
    async (supplierId, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({
            url: `api/suppliers/${supplierId}/close`,
            payload: {},
        }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return payload as Supplier
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const activateSupplierCollaboration = createAsyncThunk<Supplier, AppId, AppThunkAPIType>(
    "suppliers/activate-collaboration",
    async (supplierId, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({
            url: `api/suppliers/${supplierId}/activate`,
            payload: {},
        }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return payload as Supplier
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const suppliersSlice = createSlice({
    name: "suppliers",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(createSupplier.fulfilled, (state, action) => {
            state = adapter.addOne(state, action.payload)
        });
    
        builder.addCase(deleteSupplier.fulfilled, (state, action) => {           
            const  id  = action.payload
           
            state = adapter.removeOne(state, id);
        });
        builder.addCase(updateSupplier.fulfilled, (state, action) => {
            const { id } = action.payload;
            const changes = action.payload;
            state = adapter.updateOne(state, {
                id,
                changes,
            });
        })
        builder.addCase(loadSuppliers.pending, (state, action) => {
            if (state.state.type === "empty" || state.state.type === "error") {
                state.state = {
                    type: "loading",
                    requestId: action.meta.requestId,
                }
            }
        })
        builder.addCase(loadSuppliers.fulfilled, (state, action) => {
            if (state.state.type === "loading" && state.state.requestId === action.meta.requestId) {
                adapter.setAll(state, action.payload)
                state.state = { type: "loaded" }
            }
        })        
        builder.addCase(loadSuppliers.rejected, (state, action) => {
            if (state.state.type === "loading" && state.state.requestId === action.meta.requestId) {
                state.state = { type: "error", error: action.payload ?? unknownError(), }
            }
        })
        builder.addCase(loadSupplier.fulfilled, (state, action) => {
            adapter.upsertOne(state, action.payload)
        })
        builder.addCase(closeSupplierCollaboration.fulfilled, (state, action) => {
            adapter.updateOne(state, {
                id: action.payload.id,
                changes: action.payload,
            })
        })
        builder.addCase(activateSupplierCollaboration.fulfilled, (state, action) => {
            adapter.updateOne(state, {
                id: action.payload.id,
                changes: action.payload,
            })
        })
    },
})
export const selectSuppliers = (state: RootState): SuppliersState => state.suppliers

export const 
    { selectAll: selectAllSuppliers
    , selectById: selectSupplierById 
    } = adapter.getSelectors<RootState>(selectSuppliers)

export default suppliersSlice.reducer
